import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { apiService, resolveLoginUser } from "../../services/api";
import { SearchableDropdown } from "./SearchableDropdown";
import {
  ComponentType,
  type DropdownOption,
} from "../../services/types/ComponentTypes";
import {
  buildSettlementEntryIds,
  computeNetPosition,
  creditAvailableDebitLookup,
  filterEntries,
  formatDisplayDate,
  formatMoney,
  maxAllowedSettlement,
  normalizeUnadjustedRows,
  parseMoney,
  pendingDueLookup,
  resolveEntryId,
  resolveEntrySubtitle,
  resolveOutstanding,
  resolveReferenceNo,
  settlementColumnLabels,
  validateSettlementPair,
  type UnadjustedEntryRow,
} from "../../utils/referenceSettlementUtils";

export type ReferenceSettlementPartyType = "customer" | "vendor";

type InnerTab = "match" | "history";

type SettlementHistoryRow = {
  id?: string;
  settlement_no?: string;
  settlement_date?: string;
  settlement_amount?: number | string;
  pending_reference_no?: string;
  debit_reference_no?: string;
  credit_reference_no?: string;
  remarks?: string;
  created_by?: string;
};

interface ReferenceSettlementPanelProps {
  partyType: ReferenceSettlementPartyType;
  formData?: Record<string, any>;
  onFormDataChange?: (name: string, value: any) => void;
  showHistoryTab?: boolean;
  onSaveSuccess?: () => void;
}

const partyFieldKey = (partyType: ReferenceSettlementPartyType) =>
  partyType === "customer"
    ? "reference_settlement_customer_party_id"
    : "reference_settlement_vendor_party_id";

const partyNameKey = (partyType: ReferenceSettlementPartyType) =>
  partyType === "customer"
    ? "reference_settlement_customer_party_name"
    : "reference_settlement_vendor_party_name";

const partyLookupName = (partyType: ReferenceSettlementPartyType) =>
  partyType === "customer" ? "customers" : "supplier";

const ReferenceSettlementPanel: React.FC<ReferenceSettlementPanelProps> = ({
  partyType,
  formData,
  onFormDataChange,
  showHistoryTab = true,
  onSaveSuccess,
}) => {
  const { isDarkMode } = useDarkMode();
  const [innerTab, setInnerTab] = useState<InnerTab>("match");
  const [pendingDueRows, setPendingDueRows] = useState<UnadjustedEntryRow[]>([]);
  const [creditAvailableRows, setCreditAvailableRows] = useState<
    UnadjustedEntryRow[]
  >([]);
  const [historyRows, setHistoryRows] = useState<SettlementHistoryRow[]>([]);
  const [selectedPendingDueId, setSelectedPendingDueId] = useState("");
  const [selectedCreditAvailableId, setSelectedCreditAvailableId] =
    useState("");
  const [pendingDueSearch, setPendingDueSearch] = useState("");
  const [creditAvailableSearch, setCreditAvailableSearch] = useState("");
  const [settlementAmount, setSettlementAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [partyOptions, setPartyOptions] = useState<DropdownOption[]>([]);
  const [partyOptionsLoading, setPartyOptionsLoading] = useState(false);
  const partyOptionsLoadedRef = useRef(false);
  const onFormDataChangeRef = useRef(onFormDataChange);

  useEffect(() => {
    onFormDataChangeRef.current = onFormDataChange;
  }, [onFormDataChange]);

  const partyId = String(formData?.[partyFieldKey(partyType)] ?? "");
  const partyName = String(formData?.[partyNameKey(partyType)] ?? "");
  const addTrigger = formData?.reference_settlement_add_trigger;

  const partyLabel = partyType === "customer" ? "Customer" : "Vendor";
  const lookupName = partyLookupName(partyType);
  const columnLabels = settlementColumnLabels(partyType);

  const loadPartyOptions = useCallback(async () => {
    if (partyOptionsLoadedRef.current) {
      return;
    }
    setPartyOptionsLoading(true);
    try {
      const options = await apiService.getLookupData(lookupName, {
        parentid: "",
        lookuptype: "Select",
      });
      const normalized = Array.isArray(options) ? options : [];
      setPartyOptions(normalized);
      partyOptionsLoadedRef.current = normalized.length > 0;
    } catch {
      setPartyOptions([]);
      partyOptionsLoadedRef.current = false;
    } finally {
      setPartyOptionsLoading(false);
    }
  }, [lookupName]);

  useEffect(() => {
    partyOptionsLoadedRef.current = false;
    setPartyOptions([]);
    void loadPartyOptions();
  }, [lookupName, loadPartyOptions]);

  useEffect(() => {
    if (!addTrigger) return;
    setInnerTab("match");
    setSelectedPendingDueId("");
    setSelectedCreditAvailableId("");
    setSettlementAmount("");
    setRemarks("");
    setMessage("");
    setError("");
    onFormDataChangeRef.current?.(partyFieldKey(partyType), "");
    onFormDataChangeRef.current?.(partyNameKey(partyType), "");
  }, [addTrigger, partyType]);

  useEffect(() => {
    if (formData?.reference_settlement_inner_tab === "match") {
      setInnerTab("match");
    } else if (formData?.reference_settlement_inner_tab === "history") {
      setInnerTab("history");
    }
  }, [formData?.reference_settlement_inner_tab]);

  const resolvePartyLabel = useCallback(
    (value: string | number | boolean) => {
      const match = partyOptions.find(
        (opt) => String(opt.value) === String(value)
      );
      return match?.label ? String(match.label) : "";
    },
    [partyOptions]
  );

  const loadEntries = useCallback(async () => {
    if (!partyId) {
      setPendingDueRows([]);
      setCreditAvailableRows([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = { parentid: partyId, lookuptype: "Select" };
      const [pendingDueResp, creditAvailableResp] = await Promise.all([
        apiService.post<any[]>(
          `/pabws/utility/${pendingDueLookup(partyType)}`,
          payload,
          { errorHandling: "return-empty" }
        ),
        apiService.post<any[]>(
          `/pabws/utility/${creditAvailableDebitLookup(partyType)}`,
          payload,
          { errorHandling: "return-empty" }
        ),
      ]);

      setPendingDueRows(normalizeUnadjustedRows(pendingDueResp));
      setCreditAvailableRows(normalizeUnadjustedRows(creditAvailableResp));
    } catch (e) {
      setError(`Failed to load unadjusted entries. ${String(e)}`);
    } finally {
      setLoading(false);
    }
  }, [partyId, partyType]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiService.getModuleData("reference_settlement_summary", {
        skip: 0,
        limit: 200,
        search: [],
        sort: [{ field: "settlement_date", direction: "desc" }],
      });
      const rows = (response?.data ?? []) as SettlementHistoryRow[];
      const filtered = partyId
        ? rows.filter((row: any) => String(row.party_id ?? "") === partyId)
        : rows.filter((row: any) => {
            const pType = String(row.party_type ?? "").toLowerCase();
            return pType === partyType;
          });
      setHistoryRows(filtered);
    } catch (e) {
      setError(`Failed to load settlement history. ${String(e)}`);
    } finally {
      setLoading(false);
    }
  }, [partyId, partyType]);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    if (innerTab === "history") {
      void loadHistory();
    }
  }, [innerTab, loadHistory]);

  const filteredPendingDueRows = useMemo(
    () => filterEntries(pendingDueRows, pendingDueSearch, partyType),
    [pendingDueRows, pendingDueSearch, partyType]
  );
  const filteredCreditAvailableRows = useMemo(
    () => filterEntries(creditAvailableRows, creditAvailableSearch, partyType),
    [creditAvailableRows, creditAvailableSearch, partyType]
  );

  const pendingDueTotal = useMemo(
    () =>
      pendingDueRows.reduce((sum, row) => sum + resolveOutstanding(row), 0),
    [pendingDueRows]
  );
  const creditAvailableTotal = useMemo(
    () =>
      creditAvailableRows.reduce((sum, row) => sum + resolveOutstanding(row), 0),
    [creditAvailableRows]
  );

  const selectedPendingDue = pendingDueRows.find(
    (row) => resolveEntryId(row) === selectedPendingDueId
  );
  const selectedCreditAvailable = creditAvailableRows.find(
    (row) => resolveEntryId(row) === selectedCreditAvailableId
  );

  const settlementPairError = useMemo(
    () =>
      validateSettlementPair(
        selectedPendingDue,
        selectedCreditAvailable,
        partyType
      ),
    [selectedPendingDue, selectedCreditAvailable, partyType]
  );

  const maxSettlement = useMemo(
    () =>
      maxAllowedSettlement(
        selectedPendingDue,
        selectedCreditAvailable,
        partyType
      ),
    [selectedPendingDue, selectedCreditAvailable, partyType]
  );

  useEffect(() => {
    if (maxSettlement > 0) {
      setSettlementAmount(String(maxSettlement));
    } else {
      setSettlementAmount("");
    }
  }, [maxSettlement, selectedPendingDueId, selectedCreditAvailableId]);

  const netPosition = computeNetPosition(pendingDueTotal, creditAvailableTotal);

  const handlePartyChange = (_name: string, value: string | number | boolean) => {
    onFormDataChangeRef.current?.(partyFieldKey(partyType), value);
    const label = resolvePartyLabel(value);
    if (label) {
      onFormDataChangeRef.current?.(partyNameKey(partyType), label);
    } else {
      onFormDataChangeRef.current?.(partyNameKey(partyType), "");
    }
    setSelectedPendingDueId("");
    setSelectedCreditAvailableId("");
    setSettlementAmount("");
    setRemarks("");
    setMessage("");
    setError("");
  };

  const handleSaveSettlement = async () => {
    setError("");
    setMessage("");
    if (!partyId) {
      setError(`Select a ${partyLabel.toLowerCase()} first.`);
      return;
    }
    if (!selectedPendingDueId || !selectedCreditAvailableId) {
      setError(columnLabels.selectError);
      return;
    }

    const settlementIds = buildSettlementEntryIds(
      selectedPendingDue,
      selectedCreditAvailable,
      partyType
    );
    if ("error" in settlementIds) {
      setError(settlementIds.error);
      return;
    }

    const amount = parseMoney(settlementAmount);
    if (amount <= 0) {
      setError("Settlement amount must be greater than zero.");
      return;
    }
    if (amount > maxSettlement + 0.009) {
      setError(`Settlement amount cannot exceed ${formatMoney(maxSettlement)}.`);
      return;
    }

    setSaving(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const loginUser = resolveLoginUser();
      const payload: Record<string, unknown> = {
        data: {
          id: crypto.randomUUID(),
          party_type: partyType,
          party_id: partyId,
          debit_entry_id: settlementIds.debitEntryId,
          credit_entry_id: settlementIds.creditEntryId,
          settlement_amount: amount,
          settlement_date: today,
          remarks,
        },
      };
      if (loginUser) {
        payload.loginUser = loginUser;
      }
      const response = await apiService.put<any>(
        "/pabws/module/reference_settlement",
        payload,
        {
          includeAuthToken: true,
          authHeaderType: "Bearer",
          errorHandling: "throw",
        }
      );
      const ok =
        response?.success !== false &&
        (response?.data?.success !== false || response?.success === true);
      if (!ok && response?.message) {
        throw new Error(String(response.message));
      }
      setMessage(
        response?.message ||
          response?.data?.message ||
          "Reference settlement saved successfully."
      );
      setSelectedPendingDueId("");
      setSelectedCreditAvailableId("");
      setSettlementAmount("");
      setRemarks("");
      await loadEntries();
      if (showHistoryTab && innerTab === "history") {
        await loadHistory();
      }
      onSaveSuccess?.();
    } catch (e: any) {
      setError(String(e?.message || e || "Settlement failed."));
    } finally {
      setSaving(false);
    }
  };

  const cardBg = isDarkMode ? "#1e293b" : "#ffffff";
  const border = isDarkMode ? "#334155" : "#E5E7EB";
  const muted = isDarkMode ? "#94a3b8" : "#64748B";
  const text = isDarkMode ? "#f1f5f9" : "#0F172A";

  const renderEntryCard = (
    row: UnadjustedEntryRow,
    selectedId: string,
    onSelect: (id: string) => void
  ) => {
    const id = resolveEntryId(row);
    const isSelected = selectedId === id;
    return (
      <button
        key={id}
        type="button"
        onClick={() => onSelect(id)}
        style={{
          width: "100%",
          textAlign: "left",
          border: isSelected ? "2px solid #34D399" : `1px solid ${border}`,
          borderRadius: 12,
          background: isSelected
            ? isDarkMode
              ? "#064e3b33"
              : "#ecfdf5"
            : cardBg,
          padding: "14px 16px",
          marginBottom: 10,
          cursor: "pointer",
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: isSelected ? "5px solid #34D399" : `2px solid ${border}`,
            marginTop: 4,
            flexShrink: 0,
          }}
        />
        <span style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: text, marginBottom: 4 }}>
            {resolveReferenceNo(row)}
          </div>
          <div style={{ fontSize: 13, color: muted }}>
            {resolveEntrySubtitle(row, partyType)}
            {row.voucher_date ? ` · ${formatDisplayDate(row.voucher_date)}` : ""}
          </div>
        </span>
        <span style={{ fontWeight: 700, color: text, whiteSpace: "nowrap" }}>
          {formatMoney(resolveOutstanding(row))}
        </span>
      </button>
    );
  };

  return (
    <div
      style={{
        background: cardBg,
        border: `1px solid ${border}`,
        borderRadius: 16,
        padding: 20,
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <p style={{ margin: "4px 0 0", color: muted, fontSize: 14 }}>
          Match any open due with any available credit
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(220px, 320px) 1fr auto",
          gap: 16,
          alignItems: "end",
          marginBottom: 18,
        }}
      >
        <div>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: text, marginBottom: 6 }}>
            Select {partyLabel.toLowerCase()}
          </label>
          <SearchableDropdown
            config={{
              componentType: ComponentType.SEARCHABLE_DROPDOWN,
              name: partyType === "customer" ? "drp_customer" : "drp_vendor",
              label: partyLabel,
              lookupName,
              Json_Attribute: partyFieldKey(partyType),
              options: partyOptions,
              placeholder: partyOptionsLoading ? "Loading..." : "Search...",
            }}
            value={partyId}
            onChange={(_name, value) => {
              if (Array.isArray(value)) return;
              handlePartyChange(partyFieldKey(partyType), value);
            }}
            onFocus={() => {
              if (!partyOptionsLoadedRef.current && !partyOptionsLoading) {
                void loadPartyOptions();
              }
            }}
          />
        </div>
        <div style={{ color: muted, fontSize: 14, paddingBottom: 8 }}>
          {partyName ? (
            <>
              {partyLabel} <strong style={{ color: text }}>{partyName}</strong>
              {" · as on "}
              {formatDisplayDate(new Date().toISOString())}
            </>
          ) : (
            `Choose a ${partyLabel.toLowerCase()} to view pending dues and available credit.`
          )}
        </div>
        {partyId ? (
          <div
            style={{
              padding: "8px 14px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              background:
                netPosition.tone === "credit"
                  ? isDarkMode
                    ? "#064e3b"
                    : "#ecfdf5"
                  : netPosition.tone === "debit"
                    ? isDarkMode
                      ? "#7f1d1d"
                      : "#fef2f2"
                    : isDarkMode
                      ? "#334155"
                      : "#f8fafc",
              color:
                netPosition.tone === "credit"
                  ? isDarkMode
                    ? "#6ee7b7"
                    : "#047857"
                  : netPosition.tone === "debit"
                    ? isDarkMode
                      ? "#fca5a5"
                      : "#b91c1c"
                    : muted,
              whiteSpace: "nowrap",
            }}
          >
            {netPosition.label}
          </div>
        ) : null}
      </div>

      <div
        style={{
          display: "flex",
          gap: 24,
          borderBottom: showHistoryTab ? `1px solid ${border}` : "none",
          marginBottom: showHistoryTab ? 18 : 0,
        }}
      >
        {showHistoryTab
          ? (["match", "history"] as InnerTab[]).map((tab) => {
              const active = innerTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => {
                    setInnerTab(tab);
                    onFormDataChange?.("reference_settlement_inner_tab", tab);
                  }}
                  style={{
                    padding: "10px 0",
                    border: "none",
                    background: "transparent",
                    borderBottom: active
                      ? "2px solid #111827"
                      : "2px solid transparent",
                    fontWeight: active ? 700 : 500,
                    color: active ? text : muted,
                    cursor: "pointer",
                  }}
                >
                  {tab === "match" ? "Match references" : "Settlement history"}
                </button>
              );
            })
          : null}
      </div>

      {error ? (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 8,
            background: isDarkMode ? "#7f1d1d" : "#fef2f2",
            color: isDarkMode ? "#fecaca" : "#b91c1c",
            fontSize: 14,
          }}
        >
          {error}
        </div>
      ) : null}
      {message ? (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 8,
            background: isDarkMode ? "#064e3b" : "#ecfdf5",
            color: isDarkMode ? "#6ee7b7" : "#047857",
            fontSize: 14,
          }}
        >
          {message}
        </div>
      ) : null}

      {innerTab === "match" ? (
        <>
          {!partyId ? (
            <div
              style={{
                padding: 32,
                textAlign: "center",
                color: muted,
                border: `1px dashed ${border}`,
                borderRadius: 12,
              }}
            >
              Select a {partyLabel.toLowerCase()} to start matching references.
            </div>
          ) : loading ? (
            <div style={{ padding: 24, textAlign: "center", color: muted }}>
              Loading unadjusted entries...
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 20,
              }}
            >
              <div>
                <div style={{ fontWeight: 700, color: text, marginBottom: 10 }}>
                  {columnLabels.leftTitle} ({formatMoney(pendingDueTotal)})
                </div>
                <input
                  type="text"
                  placeholder={columnLabels.leftSearch}
                  value={pendingDueSearch}
                  onChange={(e) => setPendingDueSearch(e.target.value)}
                  style={{
                    width: "100%",
                    marginBottom: 12,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: `1px solid ${border}`,
                    background: isDarkMode ? "#0f172a" : "#fff",
                    color: text,
                  }}
                />
                {filteredPendingDueRows.length === 0 ? (
                  <div style={{ color: muted, fontSize: 14 }}>
                    {columnLabels.leftEmpty}
                  </div>
                ) : (
                  filteredPendingDueRows.map((row) =>
                    renderEntryCard(
                      row,
                      selectedPendingDueId,
                      setSelectedPendingDueId
                    )
                  )
                )}
              </div>

              <div>
                <div style={{ fontWeight: 700, color: text, marginBottom: 10 }}>
                  {columnLabels.rightTitle} ({formatMoney(creditAvailableTotal)})
                </div>
                <input
                  type="text"
                  placeholder={columnLabels.rightSearch}
                  value={creditAvailableSearch}
                  onChange={(e) => setCreditAvailableSearch(e.target.value)}
                  style={{
                    width: "100%",
                    marginBottom: 12,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: `1px solid ${border}`,
                    background: isDarkMode ? "#0f172a" : "#fff",
                    color: text,
                  }}
                />
                {filteredCreditAvailableRows.length === 0 ? (
                  <div style={{ color: muted, fontSize: 14 }}>
                    {columnLabels.rightEmpty}
                  </div>
                ) : (
                  filteredCreditAvailableRows.map((row) =>
                    renderEntryCard(
                      row,
                      selectedCreditAvailableId,
                      setSelectedCreditAvailableId
                    )
                  )
                )}
              </div>
            </div>
          )}

          {partyId && selectedPendingDueId && selectedCreditAvailableId ? (
            <div
              style={{
                marginTop: 20,
                paddingTop: 20,
                borderTop: `1px solid ${border}`,
              }}
            >
              {settlementPairError ? (
                <div
                  style={{
                    marginBottom: 12,
                    padding: "10px 12px",
                    borderRadius: 8,
                    background: isDarkMode ? "#7f1d1d" : "#fef2f2",
                    color: isDarkMode ? "#fecaca" : "#b91c1c",
                    fontSize: 14,
                  }}
                >
                  {settlementPairError}
                </div>
              ) : null}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "180px 1fr auto",
                  gap: 12,
                  alignItems: "end",
                }}
              >
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: text, marginBottom: 6 }}>
                  Settlement amount
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={settlementAmount}
                  readOnly
                  onChange={(e) => setSettlementAmount(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: `1px solid ${border}`,
                    background: isDarkMode ? "#0f172a" : "#fff",
                    color: text,
                  }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: text, marginBottom: 6 }}>
                  Remarks
                </label>
                <input
                  type="text"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Optional remarks"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: `1px solid ${border}`,
                    background: isDarkMode ? "#0f172a" : "#fff",
                    color: text,
                  }}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", alignSelf: "end" }}>
                <button
                  type="button"
                  disabled={saving || Boolean(settlementPairError) || maxSettlement <= 0}
                  onClick={() => void handleSaveSettlement()}
                  className="submit-button-theme submit-button-ring group flex items-center gap-2 text-white pl-6 pr-8 py-3 rounded-full shadow-lg transform transition-all duration-300 hover:-translate-y-1 hover:scale-105 cursor-pointer ring-2 ring-transparent disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:scale-100"
                >
                  <div className="bg-white/20 rounded-full p-1.5 group-hover:bg-white/30 transition-colors">
                    {saving ? (
                      <div className="w-6 h-6 border-2 border-transparent border-t-current rounded-full animate-spin" />
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <polyline points="17 21 17 13 7 13 7 21" />
                        <polyline points="7 3 7 8 15 8" />
                      </svg>
                    )}
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="font-bold tracking-wide text-base leading-none">
                      {saving ? "SUBMITTING..." : "SUBMIT"}
                    </span>
                    <span className="submit-button-subtitle text-[10px] font-medium opacity-80 group-hover:opacity-100">
                      Click to confirm
                    </span>
                  </div>
                </button>
              </div>
            </div>
            </div>
          ) : null}
        </>
      ) : (
        <div>
          {loading ? (
            <div style={{ padding: 24, textAlign: "center", color: muted }}>
              Loading settlement history...
            </div>
          ) : historyRows.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: muted }}>
              No settlement history found.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: muted }}>
                    <th style={{ padding: "10px 8px" }}>Settlement No.</th>
                    <th style={{ padding: "10px 8px" }}>Date</th>
                    <th style={{ padding: "10px 8px" }}>Debit ref.</th>
                    <th style={{ padding: "10px 8px" }}>Credit ref.</th>
                    <th style={{ padding: "10px 8px", textAlign: "right" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((row) => (
                    <tr key={String(row.id ?? row.settlement_no)} style={{ borderTop: `1px solid ${border}` }}>
                      <td style={{ padding: "12px 8px", color: text }}>{row.settlement_no}</td>
                      <td style={{ padding: "12px 8px", color: text }}>
                        {formatDisplayDate(String(row.settlement_date ?? ""))}
                      </td>
                      <td style={{ padding: "12px 8px", color: text }}>{row.pending_reference_no ?? row.debit_reference_no}</td>
                      <td style={{ padding: "12px 8px", color: text }}>{row.credit_reference_no}</td>
                      <td style={{ padding: "12px 8px", color: text, textAlign: "right" }}>
                        {formatMoney(parseMoney(row.settlement_amount))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReferenceSettlementPanel;
